import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import {
  Globe,
  ArrowLeft,
  Mail,
  MessageSquare,
  Send,
  CheckCircle,
  ArrowRight,
  Sparkles,
  Clock,
  HeadphonesIcon,
  Zap,
} from 'lucide-react';
import { Button, Card, CardContent, LanguageModal } from '@/components/ui';
import { SignInButton, UserButton, useAuth } from '@clerk/clerk-react';
import { PageSeo } from '@/components/seo/PageSeo';

const content = {
  hr: {
    meta: {
      title: 'Kontakt | BookiApp',
      description: 'Javite nam se s pitanjem o BookiApp-u, tehničkom podrškom, naplatom i pretplatom ili prijedlogom za suradnju.',
    },
    title: 'Kontaktirajte nas',
    subtitle: 'Tu smo za vas! Javite nam se s bilo kojim pitanjem ili prijedlogom.',
    form: {
      name: 'Vaše ime',
      email: 'Email adresa',
      subject: 'Predmet',
      subjectOptions: [
        { value: 'general', label: 'Opći upit' },
        { value: 'support', label: 'Tehnička podrška' },
        { value: 'billing', label: 'Naplata i pretplata' },
        { value: 'partnership', label: 'Suradnja' },
        { value: 'feedback', label: 'Povratna informacija' },
      ],
      message: 'Vaša poruka',
      messagePlaceholder: 'Opišite kako vam možemo pomoći...',
      submit: 'Pošalji poruku',
      sending: 'Slanje...',
    },
    success: {
      title: 'Poruka uspješno poslana!',
      message: 'Hvala vam na povjerenju. Naš tim će vam odgovoriti u najkraćem mogućem roku.',
      back: 'Pošalji novu poruku',
    },
    info: {
      email: {
        title: 'Email',
        value: 'admin@bookiapp.com',
        desc: 'Za sva pitanja i podršku',
      },
      response: {
        title: 'Vrijeme odgovora',
        value: '< 24 sata',
        desc: 'Radnim danom',
      },
      support: {
        title: 'Podrška',
        value: '24/7',
        desc: 'AI asistent dostupan uvijek',
      },
    },
    features: [
      { icon: 'zap', title: 'Brzi odgovori', desc: 'Odgovaramo u roku 24 sata' },
      { icon: 'headphones', title: 'Posvećena podrška', desc: 'Stručni tim na raspolaganju' },
      { icon: 'sparkles', title: 'AI asistent', desc: 'Instant pomoć kad trebate' },
    ],
    faq: {
      title: 'Možda tražite...',
      items: [
        { q: 'Kako započeti s BookiApp?', link: '/pricing', linkText: 'Pogledajte cijene' },
        { q: 'Kako radi prijevod chata?', link: '/about', linkText: 'Saznajte više' },
        { q: 'Imate tehničkih problema?', link: 'mailto:admin@bookiapp.com', linkText: 'Kontaktirajte podršku' },
      ],
    },
  },
  en: {
    meta: {
      title: 'Contact | BookiApp',
      description: 'Get in touch with questions about BookiApp, technical support, billing and subscriptions, or partnership ideas.',
    },
    title: 'Contact Us',
    subtitle: 'We\'re here for you! Reach out with any questions or suggestions.',
    form: {
      name: 'Your name',
      email: 'Email address',
      subject: 'Subject',
      subjectOptions: [
        { value: 'general', label: 'General inquiry' },
        { value: 'support', label: 'Technical support' },
        { value: 'billing', label: 'Billing & subscription' },
        { value: 'partnership', label: 'Partnership' },
        { value: 'feedback', label: 'Feedback' },
      ],
      message: 'Your message',
      messagePlaceholder: 'Describe how we can help you...',
      submit: 'Send message',
      sending: 'Sending...',
    },
    success: {
      title: 'Message sent successfully!',
      message: 'Thank you for reaching out. Our team will get back to you as soon as possible.',
      back: 'Send another message',
    },
    info: {
      email: {
        title: 'Email',
        value: 'admin@bookiapp.com',
        desc: 'For all questions and support',
      },
      response: {
        title: 'Response time',
        value: '< 24 hours',
        desc: 'On business days',
      },
      support: {
        title: 'Support',
        value: '24/7',
        desc: 'AI assistant always available',
      },
    },
    features: [
      { icon: 'zap', title: 'Fast responses', desc: 'We reply within 24 hours' },
      { icon: 'headphones', title: 'Dedicated support', desc: 'Expert team at your service' },
      { icon: 'sparkles', title: 'AI assistant', desc: 'Instant help when you need it' },
    ],
    faq: {
      title: 'Looking for...',
      items: [
        { q: 'How to get started with BookiApp?', link: '/pricing', linkText: 'See pricing' },
        { q: 'How does chat translation work?', link: '/about', linkText: 'Learn more' },
        { q: 'Having technical issues?', link: 'mailto:admin@bookiapp.com', linkText: 'Contact support' },
      ],
    },
  },
};

export function ContactPage() {
  const { i18n } = useTranslation();
  const lang = (i18n.language === 'hr' ? 'hr' : 'en') as 'hr' | 'en';
  const t = content[lang];

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: 'general',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [focusedField, setFocusedField] = useState<string | null>(null);

  const [langOpen, setLangOpen] = useState(false);
  const { isSignedIn } = useAuth();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    // Simulate API call
    await new Promise((resolve) => setTimeout(resolve, 1500));

    // In production, send to backend/email service
    if (import.meta.env.DEV) console.log('Contact form submitted:', formData);

    setIsSubmitting(false);
    setIsSuccess(true);
  };

  const resetForm = () => {
    setFormData({ name: '', email: '', subject: 'general', message: '' });
    setIsSuccess(false);
  };

  const featureIcons = {
    zap: Zap,
    headphones: HeadphonesIcon,
    sparkles: Sparkles,
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-[hsl(var(--background))] via-[hsl(var(--background))] to-cyan-50/30 dark:to-cyan-950/10">
      <PageSeo path="/contact" title={t.meta.title} description={t.meta.description} />
      {/* Decorative background elements */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 left-10 w-72 h-72 bg-cyan-500/10 rounded-full blur-3xl" />
        <div className="absolute top-40 right-20 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl" />
        <div className="absolute bottom-20 left-1/3 w-80 h-80 bg-indigo-500/10 rounded-full blur-3xl" />
      </div>

      {/* Header */}
      <header className="relative border-b bg-[hsl(var(--background))]/80 backdrop-blur-md sticky top-0 z-50">
        <div className="container mx-auto flex items-center justify-between p-4">
          <Link to="/" className="flex items-center gap-2 group">
            <img src="/favicon-original.png" alt="BookiApp" className="h-8 w-auto group-hover:scale-105 transition-transform" />
          </Link>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={() => setLangOpen(true)} className="gap-2" aria-haspopup="dialog" aria-expanded={langOpen}>
              <Globe className="h-4 w-4" />
              {i18n.language.toUpperCase()}
            </Button>
            {isSignedIn ? (
              <>
                <Link to="/dashboard">
                  <Button size="sm" className="bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 text-white border-0">
                    Dashboard
                  </Button>
                </Link>
                <UserButton
                  afterSignOutUrl="/"
                  appearance={{ elements: { avatarBox: 'h-9 w-9 rounded-xl ring-2 ring-transparent hover:ring-cyan-200 transition-all duration-200' } }}
                />
              </>
            ) : (
              <SignInButton mode="modal" forceRedirectUrl="/dashboard" signUpForceRedirectUrl="/dashboard">
                <Button variant="ghost" size="sm" className="hover:bg-cyan-500/10">
                  {lang === 'hr' ? 'Prijava' : 'Sign In'}
                </Button>
              </SignInButton>
            )}
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="relative container mx-auto px-4 pt-8 pb-12">
        <div className="max-w-5xl mx-auto">
          {/* Back link */}
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-[hsl(var(--muted-foreground))] hover:text-cyan-500 mb-6 transition-colors group"
          >
            <ArrowLeft className="h-4 w-4 group-hover:-translate-x-1 transition-transform" />
            {lang === 'hr' ? 'Natrag' : 'Back'}
          </Link>

          {/* Hero Section */}
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-cyan-500/10 text-cyan-600 dark:text-cyan-400 text-sm font-medium mb-6">
              <MessageSquare className="h-4 w-4" />
              {lang === 'hr' ? 'Uvijek tu za vas' : 'Always here for you'}
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black mb-6 tracking-tight">
              <span className="bg-gradient-to-r from-cyan-500 via-blue-500 to-indigo-500 bg-clip-text text-transparent">
                {t.title}
              </span>
            </h1>
            <p className="text-lg sm:text-xl text-[hsl(var(--muted-foreground))] max-w-2xl mx-auto leading-relaxed">
              {t.subtitle}
            </p>
          </div>

          {/* Feature badges */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6 mb-8">
            {t.features.map((feature, index) => {
              const Icon = featureIcons[feature.icon as keyof typeof featureIcons];
              return (
                <div
                  key={index}
                  className="group flex items-center gap-4 p-5 rounded-2xl bg-[hsl(var(--card))] border-2 border-[hsl(var(--border))] hover:border-cyan-500/50 hover:shadow-xl hover:shadow-cyan-500/10 transition-all duration-300"
                >
                  <div className="p-3 rounded-xl bg-gradient-to-br from-cyan-500/20 to-blue-500/20 group-hover:from-cyan-500/30 group-hover:to-blue-500/30 transition-colors">
                    <Icon className="h-6 w-6 text-cyan-500" />
                  </div>
                  <div>
                    <h3 className="font-bold">{feature.title}</h3>
                    <p className="text-sm text-[hsl(var(--muted-foreground))]">{feature.desc}</p>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Section divider */}
          <div className="flex items-center gap-4 mb-10">
            <div className="flex-1 h-px bg-gradient-to-r from-transparent via-[hsl(var(--border))] to-transparent" />
            <span className="text-sm font-medium text-[hsl(var(--muted-foreground))] px-4">
              {lang === 'hr' ? 'Pošaljite nam poruku' : 'Send us a message'}
            </span>
            <div className="flex-1 h-px bg-gradient-to-r from-transparent via-[hsl(var(--border))] to-transparent" />
          </div>

          <div className="grid lg:grid-cols-5 gap-8 lg:gap-12">
            {/* Contact Form */}
            <div className="lg:col-span-3">
              <Card className="overflow-hidden border-2 border-[hsl(var(--border))] hover:border-cyan-500/30 transition-colors shadow-xl">
                {/* Card gradient top border */}
                <div className="h-1 bg-gradient-to-r from-cyan-500 via-blue-500 to-indigo-500" />

                <CardContent className="p-8 pt-6">
                  {isSuccess ? (
                    <div className="text-center py-12" role="status" aria-live="polite">
                      <div className="relative w-20 h-20 mx-auto mb-6">
                        <div className="absolute inset-0 bg-emerald-500/20 rounded-full animate-ping" />
                        <div className="relative w-20 h-20 rounded-full bg-gradient-to-br from-emerald-400 to-emerald-600 flex items-center justify-center shadow-lg shadow-emerald-500/30">
                          <CheckCircle className="h-10 w-10 text-white" />
                        </div>
                      </div>
                      <h2 className="text-2xl font-bold mb-3">{t.success.title}</h2>
                      <p className="text-[hsl(var(--muted-foreground))] mb-8 max-w-md mx-auto">{t.success.message}</p>
                      <Button
                        variant="outline"
                        onClick={resetForm}
                        className="group"
                      >
                        {t.success.back}
                        <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
                      </Button>
                    </div>
                  ) : (
                    <form onSubmit={handleSubmit} className="space-y-6">
                      <div className="grid sm:grid-cols-2 gap-5">
                        <div className="space-y-2">
                          <label className="block text-sm font-semibold">{t.form.name}</label>
                          <input
                            type="text"
                            required
                            value={formData.name}
                            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                            onFocus={() => setFocusedField('name')}
                            onBlur={() => setFocusedField(null)}
                            className={`w-full px-4 py-3.5 rounded-xl border-2 bg-[hsl(var(--background))] transition-all duration-300 outline-none ${
                              focusedField === 'name'
                                ? 'border-cyan-500 shadow-lg shadow-cyan-500/20'
                                : 'border-[hsl(var(--border))] hover:border-cyan-500/50'
                            }`}
                          />
                        </div>
                        <div className="space-y-2">
                          <label className="block text-sm font-semibold">{t.form.email}</label>
                          <input
                            type="email"
                            required
                            value={formData.email}
                            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                            onFocus={() => setFocusedField('email')}
                            onBlur={() => setFocusedField(null)}
                            className={`w-full px-4 py-3.5 rounded-xl border-2 bg-[hsl(var(--background))] transition-all duration-300 outline-none ${
                              focusedField === 'email'
                                ? 'border-cyan-500 shadow-lg shadow-cyan-500/20'
                                : 'border-[hsl(var(--border))] hover:border-cyan-500/50'
                            }`}
                          />
                        </div>
                      </div>

                      <div className="space-y-2">
                        <label className="block text-sm font-semibold">{t.form.subject}</label>
                        <select
                          value={formData.subject}
                          onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                          onFocus={() => setFocusedField('subject')}
                          onBlur={() => setFocusedField(null)}
                          className={`w-full px-4 py-3.5 rounded-xl border-2 bg-[hsl(var(--background))] transition-all duration-300 outline-none cursor-pointer ${
                            focusedField === 'subject'
                              ? 'border-cyan-500 shadow-lg shadow-cyan-500/20'
                              : 'border-[hsl(var(--border))] hover:border-cyan-500/50'
                          }`}
                        >
                          {t.form.subjectOptions.map((opt) => (
                            <option key={opt.value} value={opt.value}>
                              {opt.label}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div className="space-y-2">
                        <label className="block text-sm font-semibold">{t.form.message}</label>
                        <textarea
                          required
                          rows={5}
                          placeholder={t.form.messagePlaceholder}
                          value={formData.message}
                          onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                          onFocus={() => setFocusedField('message')}
                          onBlur={() => setFocusedField(null)}
                          className={`w-full px-4 py-3.5 rounded-xl border-2 bg-[hsl(var(--background))] transition-all duration-300 outline-none resize-none ${
                            focusedField === 'message'
                              ? 'border-cyan-500 shadow-lg shadow-cyan-500/20'
                              : 'border-[hsl(var(--border))] hover:border-cyan-500/50'
                          }`}
                        />
                      </div>

                      <Button
                        type="submit"
                        disabled={isSubmitting}
                        className="w-full h-14 text-base font-semibold bg-gradient-to-r from-cyan-500 via-blue-500 to-indigo-500 hover:from-cyan-600 hover:via-blue-600 hover:to-indigo-600 text-white border-0 rounded-xl shadow-lg shadow-cyan-500/25 hover:shadow-xl hover:shadow-cyan-500/30 transition-all duration-300"
                      >
                        {isSubmitting ? (
                          <div className="flex items-center gap-2">
                            <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                            {t.form.sending}
                          </div>
                        ) : (
                          <span className="flex items-center gap-2">
                            {t.form.submit}
                            <Send className="h-5 w-5" />
                          </span>
                        )}
                      </Button>
                    </form>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Sidebar */}
            <div className="lg:col-span-2 space-y-5">
              {/* Contact Info Cards */}
              <Card className="overflow-hidden border-2 border-[hsl(var(--border))] hover:border-cyan-500/30 transition-all duration-300 hover:shadow-lg group">
                <div className="h-1 bg-gradient-to-r from-cyan-500 to-blue-500" />
                <CardContent className="p-5 pt-4">
                  <div className="flex items-center gap-4">
                    <div className="p-3 rounded-xl bg-gradient-to-br from-cyan-500 to-blue-500 shadow-lg shadow-cyan-500/25 group-hover:shadow-cyan-500/40 transition-shadow">
                      <Mail className="h-5 w-5 text-white" />
                    </div>
                    <div className="min-w-0">
                      <h3 className="font-bold">{t.info.email.title}</h3>
                      <a
                        href={`mailto:${t.info.email.value}`}
                        className="text-cyan-500 hover:text-cyan-600 font-medium transition-colors text-sm"
                      >
                        {t.info.email.value}
                      </a>
                      <p className="text-xs text-[hsl(var(--muted-foreground))] mt-0.5">{t.info.email.desc}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="overflow-hidden border-2 border-[hsl(var(--border))] hover:border-emerald-500/30 transition-all duration-300 hover:shadow-lg group">
                <div className="h-1 bg-gradient-to-r from-emerald-500 to-green-500" />
                <CardContent className="p-5 pt-4">
                  <div className="flex items-center gap-4">
                    <div className="p-3 rounded-xl bg-gradient-to-br from-emerald-500 to-green-500 shadow-lg shadow-emerald-500/25 group-hover:shadow-emerald-500/40 transition-shadow">
                      <Clock className="h-5 w-5 text-white" />
                    </div>
                    <div className="min-w-0">
                      <h3 className="font-bold">{t.info.response.title}</h3>
                      <p className="text-xl font-black text-emerald-500">{t.info.response.value}</p>
                      <p className="text-xs text-[hsl(var(--muted-foreground))]">{t.info.response.desc}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Quick Links */}
              <Card className="overflow-hidden border-2 border-[hsl(var(--border))] hover:border-indigo-500/30 transition-all duration-300 hover:shadow-lg">
                <div className="h-1 bg-gradient-to-r from-cyan-500 to-blue-500" />
                <CardContent className="p-5 pt-4">
                  <h3 className="font-bold mb-4 flex items-center gap-2">
                    <Sparkles className="h-4 w-4 text-indigo-500" />
                    {t.faq.title}
                  </h3>
                  <ul className="space-y-4">
                    {t.faq.items.map((item, i) => (
                      <li key={i} className="group/item pb-3 border-b border-[hsl(var(--border))] last:border-0 last:pb-0">
                        <p className="text-sm text-[hsl(var(--muted-foreground))] mb-1">{item.q}</p>
                        <Link
                          to={item.link}
                          className="text-sm font-semibold text-indigo-500 hover:text-indigo-600 inline-flex items-center gap-1 transition-colors"
                        >
                          {item.linkText}
                          <ArrowRight className="h-3 w-3 group-hover/item:translate-x-1 transition-transform" />
                        </Link>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="relative border-t py-8 mt-12 bg-[hsl(var(--background))]/80 backdrop-blur-sm">
        <div className="container mx-auto px-4 text-center text-sm text-[hsl(var(--muted-foreground))]">
          <p>&copy; 2026 BookiApp. {lang === 'hr' ? 'Sva prava pridržana.' : 'All rights reserved.'}</p>
        </div>
      </footer>

      <LanguageModal open={langOpen} onClose={() => setLangOpen(false)} scope="marketing" />
    </div>
  );
}

export default ContactPage;
